import { useAuthFetchWithResult } from '@e3dc-react/shell/Hooks/AuthFetch'
import { useSnackbar } from '@e3dc-react/shell/Hooks/Snackbar'
import { useQueryClient } from '@tanstack/react-query'
import { t } from 'i18next'
import { useLatestState } from 'src/Libs/flow/advanced-settings/hooks'
import { updateMyPvIo, syncInstallation } from 'src/Libs/flow/installation/api'
import { BoostConfig, MyPvIo, OptimizationMode } from 'src/Libs/flow/installation/forms'
import { installationDevicesKey } from 'src/Libs/flow/installation/hooks'
import { getMyPvPUTDto } from 'src/Libs/flow/parsers'
import { usePortalMutation } from 'src/Libs/api'

type UseBoilerReturnType = {
  isPending: boolean
  updateControlMode: (newControlMode: OptimizationMode) => void
  handleSaveBoostConfig: (newBoostConfig: BoostConfig) => void
  controlMode: OptimizationMode
}

type UseBoilerParams = {
  installationId: number
  myPv: MyPvIo
}
export const useBoiler = ({ installationId, myPv }: UseBoilerParams): UseBoilerReturnType => {
  const authFetch = useAuthFetchWithResult()
  const { enqueueErrorStack } = useSnackbar()
  const queryClient = useQueryClient()

  const [controlMode, setControlMode] = useLatestState(myPv.optimizationMode)

  const mutateControlMode = usePortalMutation({
    mutationFn: (optimizationMode: OptimizationMode) =>
      updateMyPvIo(
        installationId,
        myPv.rawData?.deviceLinkId?.toString() ?? '',
        myPv.rawData?.id?.toString() ?? '',
        getMyPvPUTDto({ ...myPv, optimizationMode }),
        authFetch
      ).then(() => syncInstallation(installationId, authFetch)),
    onMutate: value => setControlMode(value),
    onError: () => {
      enqueueErrorStack(t('common.update-failed-error'))
    },
    onSuccess: () =>
      queryClient.invalidateQueries({
        queryKey: installationDevicesKey(installationId),
      }),
  })

  const updateControlMode = (newControlMode: OptimizationMode): void => {
    mutateControlMode.mutate(newControlMode)
  }

  const mutateMyPv = usePortalMutation({
    mutationFn: (boostConfig: BoostConfig) =>
      updateMyPvIo(
        installationId,
        myPv.rawData?.deviceLinkId?.toString() ?? '',
        myPv.rawData?.id?.toString() ?? '',
        getMyPvPUTDto({ ...myPv, boostConfig }),
        authFetch
      ).then(() => syncInstallation(installationId, authFetch)),
    onSuccess: () => {
      queryClient.invalidateQueries({
        queryKey: installationDevicesKey(installationId),
      })
    },
    onError: error => enqueueErrorStack(t('common.update-failed-error'), error.message),
  })

  const handleSaveBoostConfig = (newBoostConfig: BoostConfig): void => {
    mutateMyPv.mutate(newBoostConfig)
  }

  const isPending = mutateControlMode.isPending || mutateMyPv.isPending

  return {
    isPending,
    updateControlMode,
    handleSaveBoostConfig,
    controlMode,
  }
}
